import { useState, useEffect } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { userApi, subscriptionApi } from '../lib/api';
import toast from 'react-hot-toast';
import { CreditCardIcon, CheckCircleIcon } from '@heroicons/react/24/outline';

const PLAN_DISPLAY: Record<string, { name: string; price: string | null; features: string[] }> = {
  PROFESSIONAL: {
    name: 'Professional',
    price: '$999/month',
    features: [
      'Unlimited compliance checks',
      'FDIC, NCUA, Reg Z, Reg DD, FHA, ECOA, Reg E coverage',
      'Support for images, PDFs, videos, and text',
      'UDAAP language risk detection',
      'Website compliance scanning',
      'Ad placement & CRA metadata tracking',
      'Review comments & feedback tracking',
      'Examiner XLSX spreadsheet export',
      'Examiner-ready PDF reports',
      'One-click exam packages (ZIP)',
      'Board-ready executive reports',
      'Multi-user approval workflows & RBAC',
      'Ad version change tracking',
      'Unlimited users, scans, and retention',
    ],
  },
  ENTERPRISE: {
    name: 'Enterprise',
    price: null,
    features: [
      'Everything in Professional, plus:',
      'HMDA LAR Validation (160+ edit checks)',
      'Advanced UDAAP analysis',
      'Fair lending signals & disparity analysis',
      'HMDA edit customization per institution',
      'Competitor monitoring',
      'Vendor risk management & assessments',
      'Exam-prep compliance checklists',
      'Custom data retention policies',
    ],
  },
};

export default function BillingPage() {
  const { user, setUser } = useAuthStore();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);

  // Fetch fresh user data when page loads
  const { data: freshUserData, isError, refetch } = useQuery({
    queryKey: ['user'],
    queryFn: async () => {
      const response = await userApi.getProfile();
      return response.data;
    },
  });

  // Fetch plan info with usage stats
  const { data: planData } = useQuery({
    queryKey: ['subscription-plan'],
    queryFn: async () => {
      const response = await subscriptionApi.getPlan();
      return response.data;
    },
  });

  // Update Zustand store with fresh data
  useEffect(() => {
    if (freshUserData) {
      setUser(freshUserData);
    }
  }, [freshUserData, setUser]);

  const checkoutMutation = useMutation({
    mutationFn: () => userApi.createCheckoutSession('PROFESSIONAL'),
    onSuccess: (response) => {
      window.location.href = response.data.url;
    },
    onError: () => {
      toast.error('Failed to start checkout');
      setIsLoading(false);
    },
  });

  const portalMutation = useMutation({
    mutationFn: () => userApi.createPortalSession(),
    onSuccess: (response) => {
      window.location.href = response.data.url;
    },
    onError: () => {
      toast.error('Failed to open billing portal');
      setIsLoading(false);
    },
  });

  const handleStartSubscription = () => {
    setIsLoading(true);
    checkoutMutation.mutate();
  };

  const handleManageBilling = () => {
    setIsLoading(true);
    portalMutation.mutate();
  };

  const isTrialActive = user?.subscriptionStatus === 'TRIAL';
  const isSubscriptionActive = user?.subscriptionStatus === 'ACTIVE';
  const isSubscriptionPastDue = user?.subscriptionStatus === 'PAST_DUE';
  const hasActiveSubscription = isTrialActive || isSubscriptionActive || isSubscriptionPastDue;

  const hasStripeCustomer = !!user?.stripeCustomerId;
  const userPlan = user?.subscriptionPlan || 'PROFESSIONAL';
  const isEnterprise = userPlan === 'ENTERPRISE';
  const planInfo = PLAN_DISPLAY[userPlan] || PLAN_DISPLAY.PROFESSIONAL;

  // Button visibility logic:
  // - "Subscribe Now" only if no active subscription AND not Enterprise
  // - "Manage Billing" only if Stripe customer exists (went through Stripe checkout)
  // - "Contact Sales" for Enterprise users or active subscribers without Stripe
  const showSubscribeButton = !hasActiveSubscription && !isEnterprise;
  const showManageBilling = hasStripeCustomer;
  const showContactSales = isEnterprise && !hasStripeCustomer;

  const formatDate = (date: string | Date | null | undefined) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };


  if (isError) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-navy-900">Billing & Subscription</h1>
          <p className="mt-2 text-gray-600">
            Manage your Clarid subscription and billing information
          </p>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-red-200 p-8 text-center">
          <p className="text-red-600 font-medium mb-2">Failed to load data</p>
          <p className="text-sm text-gray-500 mb-4">Please check your connection and try again.</p>
          <button onClick={() => refetch()} className="px-4 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 text-sm">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-navy-900">Billing & Subscription</h1>
        <p className="mt-2 text-gray-600">
          Manage your Clarid subscription and billing information
        </p>
      </div>

      {/* Current Plan */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden mb-6">
        <div className="px-6 py-5 border-b border-gray-200 bg-gray-50">
          <h2 className="text-lg font-semibold text-navy-900">Current Plan</h2>
        </div>
        <div className="px-6 py-6">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              {isTrialActive && (
                <>
                  <div className="flex items-center space-x-3">
                    <div className="flex-shrink-0">
                      <div className="h-12 w-12 rounded-full bg-mint-100 flex items-center justify-center">
                        <CheckCircleIcon className="h-6 w-6 text-mint-600" />
                      </div>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-navy-900">{planInfo.name} Plan</h3>
                      <p className="text-sm text-gray-600">Trial Active</p>
                    </div>
                  </div>
                  {user?.trialEndsAt && (
                    <div className="mt-4 space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Trial ends</span>
                        <span className="font-medium text-gray-900">
                          {formatDate(user.trialEndsAt)}
                        </span>
                      </div>
                    </div>
                  )}
                </>
              )}

              {isSubscriptionActive && (
                <>
                  <div className="flex items-center space-x-3">
                    <div className="flex-shrink-0">
                      <div className="h-12 w-12 rounded-full bg-mint-100 flex items-center justify-center">
                        <CheckCircleIcon className="h-6 w-6 text-mint-600" />
                      </div>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-navy-900">{planInfo.name} Plan</h3>
                      {planInfo.price ? (
                        <p className="text-sm text-gray-600">{planInfo.price}</p>
                      ) : (
                        <p className="text-sm text-gray-600">Custom pricing</p>
                      )}
                    </div>
                  </div>
                  <div className="mt-4 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Status</span>
                      <span className="font-medium text-mint-700">Active</span>
                    </div>
                    {user?.currentPeriodEnd && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Next billing date</span>
                        <span className="font-medium text-gray-900">
                          {formatDate(user.currentPeriodEnd)}
                        </span>
                      </div>
                    )}
                  </div>
                </>
              )}

              {isSubscriptionPastDue && (
                <>
                  <div className="flex items-center space-x-3">
                    <div className="flex-shrink-0">
                      <div className="h-12 w-12 rounded-full bg-amber-100 flex items-center justify-center">
                        <CreditCardIcon className="h-6 w-6 text-amber-600" />
                      </div>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-navy-900">{planInfo.name} Plan</h3>
                      <p className="text-sm text-amber-600">Payment Past Due</p>
                    </div>
                  </div>
                  <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-sm text-amber-900">
                      <strong>Your payment failed.</strong> Please update your payment method to
                      continue using Clarid.
                    </p>
                  </div>
                </>
              )}

              {!hasActiveSubscription && (
                <>
                  <div className="flex items-center space-x-3">
                    <div className="flex-shrink-0">
                      <div className="h-12 w-12 rounded-full bg-gray-100 flex items-center justify-center">
                        <CreditCardIcon className="h-6 w-6 text-gray-500" />
                      </div>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-navy-900">{planInfo.name} Plan</h3>
                      <p className="text-sm text-gray-600">
                        {user?.subscriptionStatus === 'CANCELED' ? 'Subscription canceled' : 'No active subscription'}
                      </p>
                    </div>
                  </div>
                  <div className="mt-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                    <p className="text-sm text-gray-700">
                      Subscribe to a plan to access all Clarid compliance features.
                    </p>
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="mt-6 flex space-x-4">
            {showSubscribeButton && (
              <button
                onClick={handleStartSubscription}
                disabled={isLoading}
                className="px-6 py-3 bg-ocean-600 text-white font-semibold rounded-md hover:bg-ocean-700 focus:outline-none focus:ring-2 focus:ring-ocean-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all hover:shadow-md"
              >
                {isLoading ? 'Processing...' : 'Subscribe Now'}
              </button>
            )}

            {showManageBilling && (
              <button
                onClick={handleManageBilling}
                disabled={isLoading}
                className="px-6 py-3 bg-ocean-600 text-white font-semibold rounded-md hover:bg-ocean-700 focus:outline-none focus:ring-2 focus:ring-ocean-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all hover:shadow-md"
              >
                {isLoading ? 'Loading...' : 'Manage Billing'}
              </button>
            )}

            {showContactSales && (
              <button
                onClick={() => navigate('/contact')}
                className="px-6 py-3 bg-ocean-600 text-white font-semibold rounded-md hover:bg-ocean-700 focus:outline-none focus:ring-2 focus:ring-ocean-500 focus:ring-offset-2 transition-all hover:shadow-md"
              >
                Contact Sales
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Usage Stats */}
      {planData && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden mb-6">
          <div className="px-6 py-5 border-b border-gray-200 bg-gray-50">
            <h2 className="text-lg font-semibold text-navy-900">Usage</h2>
          </div>
          <div className="px-6 py-6">
            <div className="grid grid-cols-2 gap-6">
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-600">Monthly Scans</p>
                <p className="text-2xl font-bold text-navy-900 mt-1">
                  {planData.usage?.scans ?? 0} / {planData.limits?.maxScans || '∞'}
                </p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-600">Team Members</p>
                <p className="text-2xl font-bold text-navy-900 mt-1">
                  {planData.usage?.teamMembers ?? 0} / {planData.limits?.maxUsers || '∞'}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Plan Features */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-5 border-b border-gray-200 bg-gray-50">
          <h2 className="text-lg font-semibold text-navy-900">{planInfo.name} Plan Features</h2>
        </div>
        <div className="px-6 py-6">
          <ul className="space-y-3">
            {planInfo.features.map((feature, i) => (
              <li key={i} className="flex items-start space-x-3">
                <CheckCircleIcon className="h-5 w-5 text-ocean-600 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">{feature}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Billing Portal Info */}
      {showManageBilling && (
        <div className="mt-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
          <p className="text-sm text-gray-600">
            <strong>Manage your subscription:</strong> Click "Manage Billing" to update your
            payment method, view invoices, or cancel your subscription through our secure billing
            portal powered by Stripe.
          </p>
        </div>
      )}

      {/* Enterprise billing info */}
      {isEnterprise && !hasStripeCustomer && (
        <div className="mt-6 p-4 bg-ocean-50 border border-ocean-200 rounded-lg">
          <p className="text-sm text-ocean-900">
            <strong>Enterprise billing:</strong> Your subscription is managed directly by our team.
            Contact <a href="mailto:sales@clarid.ai" className="text-ocean-600 hover:text-ocean-700 underline">sales@clarid.ai</a> for
            billing inquiries, invoices, or plan changes.
          </p>
        </div>
      )}
    </div>
  );
}
